import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  RefreshCw,
  AlertCircle,
  Newspaper,
  X,
  Save,
  Eye,
  Calendar,
  FileText,
  Upload
} from 'lucide-react';
import { useAuth } from '@/store/hooks';

interface PressRelease {
  id: string;
  title: string;
  content: string;
  summary: string;
  author: string;
  published_at: string;
  status: 'draft' | 'published' | 'archived';
  featured: boolean;
  tags: string[];
  created_at: string;
  updated_at: string;
}

interface FormData {
  title: string;
  content: string;
  summary: string;
  author: string;
  status: 'draft' | 'published' | 'archived';
  featured: boolean;
  tags: string;
  attachments: string[]; // Array of document URLs
}

const PressReleasesCMS = () => {
  const { user } = useAuth();
  const [pressReleases, setPressReleases] = useState<PressRelease[]>([]);
  const [filteredReleases, setFilteredReleases] = useState<PressRelease[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingRelease, setEditingRelease] = useState<PressRelease | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    title: '',
    content: '',
    summary: '',
    author: '',
    status: 'draft',
    featured: false,
    tags: '',
    attachments: []
  });
  const [uploadingDocuments, setUploadingDocuments] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'universal_admin' || user?.role === 'office_admin';

  useEffect(() => {
    if (isAdmin) {
      fetchPressReleases();
    }
  }, [isAdmin]);

  useEffect(() => {
    filterReleases();
  }, [pressReleases, searchTerm, statusFilter]);

  const fetchPressReleases = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/v1/press-releases?limit=100', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        const releases = (data.data || []).map((release: any) => ({
          id: release.id,
          title: release.title,
          content: release.content,
          summary: release.excerpt || release.content?.substring(0, 200) || '',
          author: 'RWA Management', // Can be updated if author field exists
          published_at: release.publish_date || release.created_at,
          status: release.status,
          featured: release.is_featured || false,
          tags: [], // Can be added if tags field exists
          created_at: release.created_at,
          updated_at: release.updated_at
        }));
        setPressReleases(releases);
      } else {
        console.error('Failed to fetch press releases');
        setPressReleases([]);
      }
    } catch (error) {
      console.error('Error fetching press releases:', error);
      setPressReleases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const filterReleases = () => {
    let filtered = pressReleases;

    if (searchTerm) {
      filtered = filtered.filter(release =>
        release.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        release.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        release.author.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(release => release.status === statusFilter);
    }

    setFilteredReleases(filtered);
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDocumentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingDocuments(true);
    try {
      const { imageUploadService } = await import('@/services/imageUploadService');
      const fileArray = Array.from(files);
      
      // Upload documents (PDFs, Word docs, etc.)
      const results = await imageUploadService.uploadMultipleImages(fileArray, 'general', true);
      
      const successfulUploads = results.filter(r => r.success);
      
      if (successfulUploads.length > 0) {
        const newUrls = successfulUploads.map(r => {
          if (r.url && (r.url.startsWith('http://') || r.url.startsWith('https://'))) {
            return r.url;
          }
          return imageUploadService.getPublicUrl(r.url);
        });
        setFormData(prev => ({
          ...prev,
          attachments: [...prev.attachments, ...newUrls]
        }));
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      alert('Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
      e.target.value = '';
    }
  };

  const removeDocument = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const releaseData = {
        title: formData.title,
        content: formData.content,
        excerpt: formData.summary,
        category: 'general', // Can be made configurable
        status: formData.status,
        is_featured: formData.featured,
        attachments: formData.attachments.length > 0 ? formData.attachments : undefined
      };

      const url = editingRelease 
        ? `/api/v1/press-releases/${editingRelease.id}`
        : '/api/v1/press-releases';
      
      const method = editingRelease ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(releaseData)
      });

      if (response.ok) {
        fetchPressReleases();
        closeModal();
      } else {
        const errorData = await response.json();
        console.error('Error saving press release:', errorData);
        alert(errorData.message || 'Failed to save press release');
      }
    } catch (error) {
      console.error('Error saving press release:', error);
      alert('Failed to save press release');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (release: PressRelease) => {
    setEditingRelease(release);
    // Parse attachments from release if available
    const releaseAny = release as any;
    const attachments = releaseAny.attachments 
      ? (typeof releaseAny.attachments === 'string' 
          ? JSON.parse(releaseAny.attachments) 
          : releaseAny.attachments)
      : [];
    
    setFormData({
      title: release.title,
      content: release.content,
      summary: release.summary,
      author: release.author,
      status: release.status,
      featured: release.featured,
      tags: release.tags.join(', '),
      attachments: Array.isArray(attachments) ? attachments : []
    });
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = async (releaseId: string) => {
    if (!window.confirm('Are you sure you want to delete this press release?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/press-releases/${releaseId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchPressReleases();
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to delete press release');
      }
    } catch (error) {
      console.error('Error deleting press release:', error);
      alert('Failed to delete press release');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRelease(null);
    setFormData({
      title: '',
      content: '',
      summary: '',
      author: '',
      status: 'draft',
      featured: false,
      tags: '',
      attachments: []
    });
  };

  const openCreateModal = () => {
    setModalMode('create');
    setShowModal(true);
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-4 sm:py-6">
            <div className="flex items-center">
              <Newspaper className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Press Releases Management</h1>
                <p className="text-sm sm:text-base text-gray-600 mt-1">Create and manage official announcements</p>
              </div>
            </div>
            <button
              onClick={openCreateModal}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Press Release
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-col sm:flex-row gap-4 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search press releases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <button
              onClick={fetchPressReleases}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Press Releases List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {isLoading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading press releases...</p>
            </div>
          ) : filteredReleases.length === 0 ? (
            <div className="p-8 text-center">
              <Newspaper className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No press releases found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'Try adjusting your search criteria.' 
                  : 'Get started by creating your first press release.'
                }
              </p>
              {!searchTerm && statusFilter === 'all' && (
                <button
                  onClick={openCreateModal}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Press Release
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredReleases.map((release) => (
                <div key={release.id} className="p-4 sm:p-6 hover:bg-gray-50">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">{release.title}</h3>
                        {release.featured && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            Featured
                          </span>
                        )}
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          release.status === 'published' 
                            ? 'bg-green-100 text-green-800'
                            : release.status === 'draft'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {release.status}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-2">{release.summary}</p>
                      <div className="flex flex-wrap items-center text-sm text-gray-500 space-x-4">
                        <span className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(release.published_at).toLocaleDateString()}
                        </span>
                        <span>By {release.author}</span>
                        {release.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1">
                            {release.tags.map((tag, index) => (
                              <span key={index} className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-4 sm:mt-0 flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(release)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(release.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-4 sm:top-10 mx-auto p-4 sm:p-0 w-full max-w-4xl">
            <div className="relative bg-white rounded-lg shadow-xl">
              <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                  {modalMode === 'create' ? 'Add Press Release' : 'Edit Press Release'}
                </h3>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter press release title"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Summary
                    </label>
                    <textarea
                      value={formData.summary}
                      onChange={(e) => handleInputChange('summary', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Brief summary of the press release"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Content *
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      rows={8}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter the full content of the press release"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Author *
                      </label>
                      <input
                        type="text"
                        value={formData.author}
                        onChange={(e) => handleInputChange('author', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter author name"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => handleInputChange('status', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                        <option value="archived">Archived</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tags
                    </label>
                    <input
                      type="text"
                      value={formData.tags}
                      onChange={(e) => handleInputChange('tags', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter tags separated by commas"
                    />
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featured"
                      checked={formData.featured}
                      onChange={(e) => handleInputChange('featured', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="featured" className="ml-2 block text-sm text-gray-900">
                      Featured press release
                    </label>
                  </div>

                  {/* Document Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Documents/PDFs
                    </label>
                    <div className="mt-1">
                      <label className="flex items-center justify-center w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                        <Upload className="w-5 h-5 mr-2 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {uploadingDocuments ? 'Uploading...' : 'Upload Documents (PDF, Word, etc.)'}
                        </span>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleDocumentUpload}
                          disabled={uploadingDocuments}
                          className="hidden"
                        />
                      </label>
                    </div>
                    {formData.attachments.length > 0 && (
                      <div className="mt-3 space-y-2">
                        {formData.attachments.map((url, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                            <div className="flex items-center flex-1 min-w-0">
                              <FileText className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:text-blue-800 truncate"
                              >
                                {url.split('/').pop() || `Document ${index + 1}`}
                              </a>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeDocument(index)}
                              className="ml-2 text-red-600 hover:text-red-800"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-6 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        {modalMode === 'create' ? 'Creating...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        {modalMode === 'create' ? 'Create Release' : 'Update Release'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PressReleasesCMS;
